<script lang="ts" setup generic="TRow extends Record<string, unknown>">
import { getSortIndicator } from '@/utils/tables/sorting-data';
import { useResizeObserver } from '@vueuse/core';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type {
  PaginationTableColumn,
  PaginationTableViewAction,
  PaginationTableViewProps,
} from '../types';

const { t } = useI18n();

const props = defineProps<PaginationTableViewProps<TRow>>();
const emit = defineEmits<{
  (event: 'action', payload: PaginationTableViewAction): void;
  (event: 'row-click', row: TRow): void;
}>();

const skeletonRowCount = computed(() => {
  if (!showSkeleton.value) return 0;
  return Math.max(0, props.pageSize - props.rows.length);
});

const showSkeleton = ref(props.isLoading ?? false);
const isFadingOut = ref(false);

watch(
  () => props.isLoading,
  (newVal) => {
    if (newVal) {
      showSkeleton.value = true;
      isFadingOut.value = false;
    } else {
      isFadingOut.value = true;
      setTimeout(() => {
        showSkeleton.value = false;
        isFadingOut.value = false;
      }, 500);
    }
  },
);

const tableContainerStyle = computed(() => {
  if (!showSkeleton.value) return {};
  if (skeletonRowCount.value === 0) return { '--skeleton-fade-start': '100%' };
  const realRatio = props.rows.length / props.pageSize;
  const fadeStart = Math.round(realRatio * 100);
  return { '--skeleton-fade-start': `${fadeStart}%` };
});

// Width that fits the widest page number at this font size (0.75rem = is-small).
// 1 ch ≈ one digit width; add padding for the button itself.
const pageButtonWidth = computed(() => {
  const digits = String(props.totalPages).length;
  return `calc(${digits}ch + 1.5em)`;
});

function onColumnClick(column: PaginationTableColumn<TRow>) {
  if (!column.sortable) return;
  emit('action', { type: 'column-sort', payload: { columnKey: column.key } });
}

function onPageChange(page: number) {
  emit('action', { type: 'page-change', payload: { page } });
}

function onPageSizeChange(event: Event) {
  const pageSize = Number((event.target as HTMLSelectElement).value);
  emit('action', { type: 'page-size-change', payload: { pageSize } });
}

function onRowClick(row: TRow) {
  if (window.getSelection()?.toString().length) return;
  emit('row-click', row);
}

// Top scrollbar mirror
const mirrorRef = ref<HTMLElement | null>(null);
const containerRef = ref<HTMLElement | null>(null);
const tableInnerRef = ref<HTMLElement | null>(null);
const mirrorContentWidth = ref(0);

// Watch the inner <table> element — its bounding box changes when columns are
// added/removed. The container div stays the same size (overflow-x: auto clips
// it), so observing the container would miss those updates.
useResizeObserver(tableInnerRef, () => {
  mirrorContentWidth.value = containerRef.value?.scrollWidth ?? 0;
});

let syncingFromMirror = false;
let syncingFromContainer = false;

function onMirrorScroll() {
  if (syncingFromContainer) return;
  syncingFromMirror = true;
  if (containerRef.value && mirrorRef.value) {
    containerRef.value.scrollLeft = mirrorRef.value.scrollLeft;
  }
  syncingFromMirror = false;
}

function onContainerScroll() {
  if (syncingFromMirror) return;
  syncingFromContainer = true;
  if (mirrorRef.value && containerRef.value) {
    mirrorRef.value.scrollLeft = containerRef.value.scrollLeft;
  }
  syncingFromContainer = false;
}
</script>

<template>
  <div class="pagination-table">
    <div
      class="pagination-table__controls is-flex is-justify-content-flex-end is-align-items-center"
      style="gap: 1rem"
    >
      <div class="is-flex is-align-items-center">
        <span class="mr-2">{{ t('paginationTable.rowsPerPage') }}:</span>
        <div class="select is-small">
          <select :value="pageSize" @change="onPageSizeChange">
            <option
              v-for="option in pageSizeOptions"
              :key="option"
              :value="option"
            >
              {{ option }}
            </option>
          </select>
        </div>
      </div>

      <div class="buttons are-small mb-0">
        <button
          class="button"
          :disabled="currentPage <= 1"
          aria-label="Previous page"
          @click="onPageChange(currentPage - 1)"
        >
          &lsaquo;
        </button>
        <button
          v-for="(page, index) in pageNumbers"
          :key="index"
          class="button"
          :class="{
            'is-active': page === currentPage,
            'is-static': page === '...',
          }"
          :style="{ width: pageButtonWidth, justifyContent: 'center' }"
          @click="page !== '...' && onPageChange(page)"
        >
          {{ page }}
        </button>
        <button
          class="button"
          :disabled="currentPage >= totalPages"
          aria-label="Next page"
          @click="onPageChange(currentPage + 1)"
        >
          &rsaquo;
        </button>
      </div>
    </div>

    <div class="is-flex is-justify-content-flex-end mb-2 mt-2">
      <span class="has-text-grey is-size-7">
        {{ rangeStart }}&ndash;{{ rangeEnd }} {{ t('paginationTable.of') }}
        {{ totalItems }}
      </span>
    </div>

    <div ref="mirrorRef" class="scroll-mirror" @scroll.passive="onMirrorScroll">
      <div :style="{ width: mirrorContentWidth + 'px', height: '1px' }" />
    </div>

    <div
      ref="containerRef"
      class="table-container"
      :class="{ 'is-loading': showSkeleton, 'is-fading-out': isFadingOut }"
      :style="tableContainerStyle"
      @scroll.passive="onContainerScroll"
    >
      <table class="table is-fullwidth" ref="tableInnerRef">
        <thead>
          <tr>
            <th
              v-for="column in columns"
              :key="column.key"
              :class="[{ sortable: column.sortable }, column.align]"
              :style="{
                width: column.columnWidth ?? undefined,
                verticalAlign: 'middle',
              }"
              @click="onColumnClick(column)"
            >
              <slot :name="`header-${column.key}`">
                <span class="header-text">{{ column.label }}</span>
              </slot>
              <span
                v-if="column.sortable && columnSortDirections[column.key]"
                class="sort-indicator"
                :class="{
                  'is-asc': columnSortDirections[column.key] === 'asc',
                  'is-desc': columnSortDirections[column.key] === 'desc',
                }"
              >
                {{ getSortIndicator(columnSortDirections[column.key]!) }}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, rowIndex) in rows"
            :key="rowIndex"
            :class="rowClass?.(row)"
            @click="onRowClick(row)"
          >
            <td
              v-for="column in columns"
              :key="column.key"
              :class="column.align"
            >
              <slot
                name="cell"
                :row="row"
                :column="column.key as keyof TRow"
                :value="
                  column.render
                    ? column.render(row)
                    : row[column.key as keyof TRow]
                "
              >
                {{
                  column.render
                    ? column.render(row)
                    : row[column.key as keyof TRow]
                }}
              </slot>
            </td>
          </tr>
          <tr v-if="rows.length === 0 && !isLoading">
            <td
              :colspan="columns.length"
              class="has-text-centered has-text-grey py-4"
            >
              {{ t('paginationTable.noData') }}
            </td>
          </tr>
          <tr
            v-for="(_, i) in skeletonRowCount"
            :key="'skeleton-' + i"
            class="skeleton-row"
            :class="{ 'skeleton-row--fading': isFadingOut }"
            :style="{ opacity: 1 }"
            aria-hidden="true"
          >
            <td v-for="column in columns" :key="column.key">
              <div class="skeleton-cell" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.scroll-mirror {
  overflow-x: auto;
  overflow-y: hidden;
  border-radius: 8px 8px 0 0;
}

.table-container {
  border: 1px solid var(--light-grey);
  border-radius: 8px;
  overflow-x: auto;
  overflow-y: hidden;
  position: relative;
  /* Hide the native bottom scrollbar — the top mirror is the only one shown */
  scrollbar-width: none;
}

.table-container::-webkit-scrollbar {
  display: none;
}

.table-container.is-loading::after,
.table-container.is-fading-out::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 8px;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0) var(--skeleton-fade-start, 0%),
    white 100%
  );
  pointer-events: none;
  z-index: 2;
}

.table-container.is-fading-out::after {
  animation: overlay-fade-out 0.5s ease forwards;
}

@keyframes overlay-fade-out {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

.pagination-table {
  font-size: 0.85rem;
}

.pagination-table :deep(table) {
  margin-bottom: 0;
}

.pagination-table :deep(table tbody tr:last-child td:first-child) {
  border-bottom-left-radius: 8px;
}

.pagination-table :deep(table tbody tr:last-child td:last-child) {
  border-bottom-right-radius: 8px;
}

.skeleton-row--fading td {
  animation: skeleton-row-fade-out 0.5s ease forwards;
}

@keyframes skeleton-row-fade-out {
  from {
    opacity: 1;
    border-color: transparent;
  }
  to {
    opacity: 0;
    border-color: transparent;
  }
}

.skeleton-row td {
  border-color: transparent !important;
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
}

.skeleton-cell {
  height: 1em;
  border-radius: 4px;
  background: linear-gradient(90deg, #ebebeb 25%, #f5f5f5 50%, #ebebeb 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.4s ease-in-out infinite;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
